/**
 * /tools/password-breach — client-side HIBP k-anonymity lookup.
 * Password is hashed in the browser (SubtleCrypto SHA-1) — only first 5 hex
 * chars of the hash leave the device. Nothing is logged on our servers.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    KeyRound, Eye, EyeOff, ShieldAlert, CheckCircle2, Loader2,
    ChevronDown, ChevronUp, ShieldCheck, Lock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { postJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";

async function sha1Hex(text) {
    const enc = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest("SHA-1", enc);
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase();
}

function strengthHint(pw) {
    if (!pw) return null;
    let score = 0;
    if (pw.length >= 8)  score++;
    if (pw.length >= 12) score++;
    if (pw.length >= 16) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^\w\s]/.test(pw)) score++;
    // Common-pattern penalties
    if (/^(password|qwerty|123|admin|letmein)/i.test(pw)) score = Math.max(0, score - 3);
    const label = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong", "Excellent"][Math.min(score, 6)];
    const color = score <= 1 ? "text-accent-red" : score <= 3 ? "text-accent-amber" : "text-accent-mint";
    return { score, label, color };
}

const FAQ = [
    {
        q: "How does this work without my password leaving my browser?",
        a: "We hash your password with SHA-1 inside your browser, then send only the FIRST 5 characters of the hash to Have I Been Pwned's k-anonymity API. HIBP returns every breach hash starting with those 5 chars (~500 candidates). The match check happens locally. Your full password and full hash never leave your device.",
    },
    {
        q: "Why SHA-1 if it's broken?",
        a: "HIBP uses SHA-1 because the goal is matching across 700M+ historical breach records, not securing against new attacks. The k-anonymity protocol means even SHA-1 is fine here — your password is never transmitted.",
    },
    {
        q: "Are you logging the passwords I check?",
        a: "No. Only an anonymous 'password-breach-check' event is recorded (no password data, no hash, no IP-tied identity). Look at the Network tab — you'll see exactly what's sent.",
    },
    {
        q: "What does the breach count actually mean?",
        a: "The number is how many times that exact password string has appeared in publicly-leaked breach corpora. Any non-zero count means attackers already have it in credential-stuffing wordlists. Change it everywhere it's reused.",
    },
];

export const PasswordBreach = () => {
    usePageMeta({
        title: "Free Password Breach Check (k-anonymity) — Cybersecurity Forefront",
        description:
            "Check if your password has appeared in known data breaches. Privacy-first: the password never leaves your browser. Uses the Have I Been Pwned k-anonymity API.",
        jsonLd: {
            "@context": "https://schema.org", "@type": "WebApplication",
            name: "Password Breach Check", applicationCategory: "SecurityApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
    });

    const [pw, setPw] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [openFaq, setOpenFaq] = useState(null);

    const strength = strengthHint(pw);

    const submit = async (e) => {
        e?.preventDefault();
        if (!pw) return;
        setBusy(true);
        setResult(null);
        try {
            const hash = await sha1Hex(pw);
            const prefix = hash.slice(0, 5);
            const suffix = hash.slice(5);
            const r = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
                headers: { "Add-Padding": "true" },
            });
            if (!r.ok) throw new Error("HIBP API error");
            const text = await r.text();
            const lines = text.split("\n");
            let count = 0;
            for (const line of lines) {
                const [sfx, c] = line.trim().split(":");
                if (sfx && sfx.toUpperCase() === suffix) {
                    count = parseInt(c, 10) || 0;
                    break;
                }
            }
            setResult({ count, prefix });
            // Fire-and-forget telemetry (NO password data)
            postJSON("/tools/usage", {
                tool_id: "password-breach", action: "password-breach-check",
                details: { breached: count > 0, count_bucket: count === 0 ? "none" : count > 100000 ? "huge" : count > 1000 ? "many" : "few" },
            }).catch(() => {});
        } catch (e) {
            setResult({ error: e?.message || "Lookup failed" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-16" data-testid="password-breach-page">
            <div className="max-w-2xl mx-auto px-5">
                <ToolPageMeta toolId="password-breach" />
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free Tool</p>
                <h1 className="mt-2 font-display font-semibold text-4xl sm:text-5xl tracking-tight">
                    Has this password leaked?
                </h1>
                <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-xl">
                    We hash your password in <em>your browser</em> and only send the first 5 characters of the SHA-1 hash to the
                    Have I Been Pwned k-anonymity API. Nothing is logged on our servers.
                </p>

                {/* Privacy banner */}
                <div className="mt-5 rounded-xl border border-accent-mint/30 bg-accent-mint/[0.05] p-4 flex items-start gap-3" data-testid="privacy-notice">
                    <Lock className="w-4 h-4 text-accent-mint mt-0.5 shrink-0" />
                    <div className="text-[12.5px] text-text-muted leading-relaxed">
                        <strong className="text-accent-mint">Your password never leaves this page.</strong> SHA-1 hash → keep first 5 chars →
                        send only those to <code className="font-mono text-accent-cyan">api.pwnedpasswords.com/range/&lt;prefix&gt;</code>.
                        Local-only suffix match. Open DevTools → Network and verify.
                    </div>
                </div>

                <form onSubmit={submit} className="mt-7 flex gap-2 flex-wrap items-stretch" data-testid="pw-form">
                    <div className="flex-1 min-w-[200px] relative">
                        <input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)}
                            placeholder="Type a password to check" data-testid="pw-input"
                            autoComplete="off"
                            className="w-full px-4 py-3 pr-11 bg-surface border border-white/[0.08] rounded-md text-[14px] font-mono focus:outline-none focus:border-accent-cyan" />
                        <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide" : "Show"}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-text-dim hover:text-accent-cyan p-2"
                            data-testid="pw-toggle">
                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <button type="submit" disabled={busy || !pw} data-testid="pw-submit"
                        className="inline-flex items-center gap-1.5 px-5 py-3 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50">
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : <><KeyRound className="w-4 h-4" /> Check</>}
                    </button>
                </form>

                {strength && (
                    <div className="mt-3 flex items-center gap-2 text-[12px]" data-testid="pw-strength">
                        <span className="text-text-dim font-mono uppercase tracking-wider">Strength</span>
                        <span className={`font-mono ${strength.color}`}>{strength.label}</span>
                        <div className="flex-1 h-1 bg-white/[0.05] rounded overflow-hidden">
                            <div className={[
                                "h-full transition-all",
                                strength.score <= 1 ? "bg-accent-red w-1/6" :
                                strength.score <= 3 ? "bg-accent-amber w-3/6" :
                                strength.score <= 4 ? "bg-accent-mint w-4/6" : "bg-accent-mint w-6/6",
                            ].join(" ")} />
                        </div>
                    </div>
                )}

                {result && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className="mt-7" data-testid="pw-result">
                        {result.error ? (
                            <div className="rounded-xl border border-accent-red/30 bg-accent-red/[0.05] p-5 text-[13.5px]">
                                Lookup failed: {result.error}
                            </div>
                        ) : result.count > 0 ? (
                            <div className="rounded-xl border border-accent-red/40 bg-accent-red/[0.08] p-5" data-testid="pw-breach-result">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShieldAlert className="w-5 h-5 text-accent-red" />
                                    <h3 className="font-display font-semibold text-[16px] text-accent-red">
                                        Found in {result.count.toLocaleString()} breaches
                                    </h3>
                                </div>
                                <p className="text-[13px] text-text-muted leading-relaxed">
                                    This password appears in known data breaches. Change it everywhere it is reused. Consider a password
                                    manager + unique passphrase or generated random password (16+ chars).
                                </p>
                                <Link to="/contact" className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan">
                                    Want password training for your team? Talk to us →
                                </Link>
                            </div>
                        ) : (
                            <div className="rounded-xl border border-accent-mint/40 bg-accent-mint/[0.05] p-5" data-testid="pw-clean-result">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 className="w-5 h-5 text-accent-mint" />
                                    <h3 className="font-display font-semibold text-[16px] text-accent-mint">Not found in any known breach</h3>
                                </div>
                                <p className="text-[13px] text-text-muted leading-relaxed">
                                    Good news — this exact string isn't in HIBP's 850M+ breach records. That doesn't make it strong though.
                                    Length and entropy still matter. Check the strength meter above.
                                </p>
                            </div>
                        )}
                    </motion.div>
                )}

                <section className="mt-16 prose prose-invert max-w-none">
                    <h2 className="font-display font-semibold text-2xl tracking-tight">Privacy & how k-anonymity works</h2>
                    <p className="text-[14px] text-text-muted leading-relaxed mt-3">
                        Cloudflare and Have I Been Pwned built the
                        <a className="text-accent-cyan" target="_blank" rel="noreferrer" href="https://en.wikipedia.org/wiki/K-anonymity"> k-anonymity range API</a> precisely
                        to let services ask "is this password leaked?" without ever revealing the password. You compute SHA-1,
                        keep the first 5 hex chars, send those to <code>api.pwnedpasswords.com/range/&lt;prefix&gt;</code>, and locally
                        check whether the suffix of your hash appears among the ~500 candidates returned. Cybersecurity Forefront
                        does not run a proxy — your browser talks directly to the API.
                    </p>
                    <h3 className="mt-6 font-display font-semibold text-xl">Frequently asked questions</h3>
                    <div className="mt-3 space-y-2">
                        {FAQ.map((f, i) => (
                            <div key={i} className="rounded-md border border-white/[0.06] bg-surface/30">
                                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between px-4 py-3 text-left">
                                    <span className="text-[13.5px] font-medium">{f.q}</span>
                                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-text-dim" /> : <ChevronDown className="w-4 h-4 text-text-dim" />}
                                </button>
                                {openFaq === i && <div className="px-4 pb-3 text-[13px] text-text-muted">{f.a}</div>}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default PasswordBreach;
